// The drive's window: a title bar and the file browser, in the page beside
// the pinned cross. The manifest loads as the window nears the viewport (or
// when show() asks for it first); a failure shows in the window with a
// retry, never silently.
import { createDriveBrowser } from "./drive-browser.js";
import { h, loadManifest } from "./files.js";
import { createWindow } from "./window.js";

const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");

/**
 * baseUrl     where the photos live: their folder (manifest.json + img/),
 *             e.g. "photos/", or their manifest's URL (files.js, manifestUrl)
 * driveName   the volume label in the title bar
 * capacity    label for the title bar, e.g. "64 GB"
 * viewer      from createViewer(), for opening photos
 */
export function createDriveWindow({ baseUrl, driveName, capacity, viewer }) {
  // the window every window on the page is (window.js), as a section of the
  // page headed by the drive's name
  const win = createWindow({
    tag: "section",
    barTag: "header",
    titleTag: "h2",
    kind: "files",
    title: [h("span.led", { "aria-hidden": "true" }), driveName],
    titleAttrs: { id: "drive-title" },
    meta: capacity,
    attrs: { id: "drive", "aria-labelledby": "drive-title" },
  });
  const { el, body } = win;
  el.classList.add("dwin");
  win.bar.classList.add("dwin__bar");
  win.title.classList.add("dwin__title");
  win.bar.querySelector(".win__meta").classList.add("dwin__meta");
  body.classList.add("dwin__body");
  let browser = null;
  let loading = null;

  // swaps the window's contents, keeping focus in the window if it was there
  function replaceBody(content, focusTarget) {
    const hadFocus = el.contains(document.activeElement);
    body.replaceChildren(content);
    if (hadFocus) focusTarget.focus({ preventScroll: true });
  }

  // reads the drive ahead of the window coming into view: once, whatever
  // starts the first read (a failed read waits for Retry or show())
  const preload = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) load();
  }, { rootMargin: "0px 0px 100% 0px" });
  preload.observe(el);

  function load() {
    preload.disconnect();
    // focusable so focus has somewhere to be while the drive is read
    const status = h("p.dwin__note.caps", { role: "status", tabindex: "-1", text: "Reading drive" });
    replaceBody(status, status);
    loading = loadManifest(baseUrl).then(
      (manifest) => {
        browser = createDriveBrowser({ manifest, viewer });
        replaceBody(browser.el, browser.pane);
      },
      (err) => {
        console.error(err);
        loading = null;
        const retry = h("button.dwin__retry.caps", { type: "button", text: "Retry", onclick: () => load() });
        replaceBody(h("div.dwin__note.dwin__note--error", { role: "alert" }, h("p", { text: err.message }), retry), retry);
      },
    );
  }

  return {
    el,
    /** Scrolls the window to its resting place and moves focus into it. */
    show() {
      if (!loading) load();
      el.scrollIntoView({ behavior: reducedMotion.matches ? "instant" : "smooth", block: "start" });
      (browser?.pane ?? body.querySelector("[tabindex], button")).focus({ preventScroll: true });
    },
  };
}
